import { useTranslation } from 'react-i18next';
import { FieldError } from '~/components/ui/inputs/FieldError';
import { SectionPanelHeader } from '~/components/ui/SectionPanel';
import { CellEditPopover } from '~/components/ui/table/CellEditPopover';
import { EditCellForm } from '~/components/ui/table';
import { UnitCode, type Quantity } from '~/graphql/graphql';
import { formatQuantityCell } from '~/utils';
import { getVehiclePayload, type VehicleCapacityPayload } from '~/utils/order';
import { useInlineEdit } from '~/pages/admin-inventory-classification/useInlineEdit';
import {
  capacityMessageParams,
  wholeCapacityMessageParams,
  parseCapacityInput,
} from '~/utils/vehicleCapacityBounds';

type Capacity = Pick<Quantity, 'value' | 'unit'>;

type SaveCapacity = (vehicleId: string, capacity: number) => Promise<boolean>;

type CapacityEditProps = Readonly<{
  suffix: string;
  unit: UnitCode;
  vehicle: { id: string; name: string };
  capacity: Capacity | null;
  onSave: SaveCapacity;
}>;

function CapacityEdit({
  suffix,
  unit,
  vehicle,
  capacity,
  onSave,
}: CapacityEditProps) {
  const { t } = useTranslation('inventory-classification');
  const edit = useInlineEdit(
    (raw) => parseCapacityInput(raw, unit),
    (value) => onSave(vehicle.id, value)
  );

  return (
    <>
      <CellEditPopover
        isOpen={edit.isOpen}
        onOpen={edit.open}
        onDone={edit.close}
        isBusy={edit.isSaving}
        columnLabel={t('capacity.edit_label', { vehicle: vehicle.name })}
        testId={`edit-product-capacity-${suffix}`}
        renderEditor={() => (
          <EditCellForm
            defaultValue={capacity === null ? '' : capacity.value}
            editableType="number"
            isSubmitting={edit.isSaving}
            onSubmit={edit.submit}
            onCancel={edit.close}
            testIds={{
              input: `product-capacity-input-${suffix}`,
              submit: `product-capacity-submit-${suffix}`,
            }}
          />
        )}
      />
      {edit.hasError ? (
        <FieldError testId={`product-capacity-error-${suffix}`}>
          {unit === UnitCode.Pcs
            ? t('capacity.error_whole', wholeCapacityMessageParams)
            : t('capacity.error', capacityMessageParams)}
        </FieldError>
      ) : null}
    </>
  );
}

type VehicleCapacityRowProps = Readonly<{
  productId: string;
  unit: UnitCode;
  vehicle: { id: string; name: string };
  capacity: Capacity | null;
  onSave: SaveCapacity | null;
}>;

function VehicleCapacityRow({
  productId,
  unit,
  vehicle,
  capacity,
  onSave,
}: VehicleCapacityRowProps) {
  const { t } = useTranslation('inventory-classification');
  const suffix = `${productId}-${vehicle.id}`;

  return (
    <li className="flex items-center justify-between px-4 py-2">
      <span className="text-sm text-neut-prim">{vehicle.name}</span>
      <div className="flex items-center gap-2 text-sm text-neut-prim">
        <span data-testid={`product-capacity-${suffix}`}>
          {capacity === null
            ? t('capacity.not_set')
            : formatQuantityCell(capacity.value, capacity.unit)}
        </span>
        {onSave ? (
          <CapacityEdit
            suffix={suffix}
            unit={unit}
            vehicle={vehicle}
            capacity={capacity}
            onSave={onSave}
          />
        ) : null}
      </div>
    </li>
  );
}

type ProductVehicleCapacityEditorProps = Readonly<{
  productId: string;
  unit: UnitCode;
  vehicles: readonly { id: string; name: string }[];
  capacities: VehicleCapacityPayload[];
  onSave: SaveCapacity | null;
}>;

export function ProductVehicleCapacityEditor({
  productId,
  unit,
  vehicles,
  capacities,
  onSave,
}: ProductVehicleCapacityEditorProps) {
  const { t } = useTranslation('inventory-classification');

  if (vehicles.length === 0) {
    return null;
  }

  return (
    <div className="border-t border-neut-border">
      <SectionPanelHeader
        className="border-b border-neut-border"
        title={t('capacity.title')}
      />
      <ul className="divide-y divide-neut-border">
        {vehicles.map((vehicle) => (
          <VehicleCapacityRow
            key={vehicle.id}
            productId={productId}
            unit={unit}
            vehicle={vehicle}
            capacity={getVehiclePayload(capacities, vehicle.id)}
            onSave={onSave}
          />
        ))}
      </ul>
    </div>
  );
}
